import { Shield, Award, Building2, Globe, FlaskConical, BadgeCheck } from 'lucide-react';

const certifications = [
  { icon: Shield, label: 'ISO 17025', desc: 'Accredited Testing' },
  { icon: Award, label: 'cGMP-Aligned', desc: 'Manufacturing Standards' },
  { icon: BadgeCheck, label: 'HPLC >99%', desc: 'Purity Guaranteed' },
  { icon: FlaskConical, label: 'LC-MS Verified', desc: 'Identity Confirmed' },
];

const partners = [
  { type: 'Pharmaceutical R&D', region: 'Germany', desc: 'Top-10 EU pharmaceutical developer' },
  { type: 'Cosmetic Manufacturer', region: 'France', desc: 'Leading cosmeceutical formulator' },
  { type: 'Research Institute', region: 'Netherlands', desc: 'Academic peptide research center' },
  { type: 'Supplement Producer', region: 'United Kingdom', desc: 'GMP-certified nutraceutical manufacturer' },
  { type: 'Biotech Startup', region: 'Switzerland', desc: 'Clinical-stage metabolic therapeutics' },
  { type: 'Contract Manufacturer', region: 'Belgium', desc: 'Full-service CDMO partner' },
];

const stats = [
  { value: '37+', label: 'Peptide APIs' },
  { value: '200+', label: 'B2B Clients Served' },
  { value: '12', label: 'EU Countries' },
  { value: '99.4%', label: 'Avg. Batch Purity' },
];

export default function TrustSignals() {
  return (
    <section data-testid="trust-signals-section" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="mb-16">
          <span
            className="text-xs tracking-widest uppercase text-[#0A7C84] mb-3 block"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Why Biolyn
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-4"
            style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
            data-testid="trust-heading"
          >
            Trusted Across
            <br />
            Europe
          </h2>
          <p
            className="text-base md:text-lg text-[#495057] max-w-2xl"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Pharmaceutical developers, research institutions, and cosmetic manufacturers rely on Biolyn for consistent quality and reliable supply.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#DEE2E6] mb-16" data-testid="trust-stats">
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className={`p-8 text-center ${idx < 3 ? 'border-r border-[#DEE2E6]' : ''}`}
              data-testid={`trust-stat-${idx}`}
            >
              <span
                className="text-3xl md:text-4xl text-[#0A1128] block mb-1"
                style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              >
                {stat.value}
              </span>
              <span
                className="text-[10px] tracking-widest uppercase text-[#A0AAB2]"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#DEE2E6] mb-16" data-testid="trust-certifications">
          {certifications.map((cert, idx) => (
            <div
              key={cert.label}
              className={`p-6 ${idx < 3 ? 'border-r border-[#DEE2E6]' : ''}`}
              data-testid={`certification-${idx}`}
            >
              <cert.icon className="w-5 h-5 text-[#0A7C84] mb-3" strokeWidth={1.5} />
              <span
                className="text-sm text-[#0A1128] block mb-0.5"
                style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
              >
                {cert.label}
              </span>
              <span className="text-xs text-[#6C757D]" style={{ fontFamily: 'var(--font-mono)' }}>
                {cert.desc}
              </span>
            </div>
          ))}
        </div>

        {/* Partner Types Grid */}
        <div className="mb-8">
          <span
            className="text-[10px] tracking-widest uppercase text-[#A0AAB2] mb-6 block"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Trusted By Organizations Including
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#DEE2E6]" data-testid="trust-partners">
          {partners.map((partner, idx) => (
            <div
              key={partner.type}
              className={`p-6 ${idx % 3 !== 2 ? 'lg:border-r border-[#DEE2E6]' : ''} ${idx % 2 !== 1 ? 'sm:border-r lg:border-r-0 border-[#DEE2E6]' : ''} ${idx < 3 ? 'border-b border-[#DEE2E6]' : ''} ${idx < 4 ? 'sm:border-b border-[#DEE2E6]' : ''}`}
              data-testid={`partner-${idx}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-4 h-4 text-[#A0AAB2]" strokeWidth={1.5} />
                <div className="flex items-center gap-2">
                  <Globe className="w-3 h-3 text-[#A0AAB2]" strokeWidth={1.5} />
                  <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
                    {partner.region}
                  </span>
                </div>
              </div>
              <span
                className="text-sm text-[#0A1128] block mb-1"
                style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
              >
                {partner.type}
              </span>
              <span className="text-xs text-[#6C757D]" style={{ fontFamily: 'var(--font-body)' }}>
                {partner.desc}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
